import { apiRequest } from '../../../services/api/client';
import { ClassVisitorRecord } from '../mocks/classVisitors';

type VisitorResource = {
  id: number;
  class_lesson_id: number;
  visited_on: string | null;
  full_name: string;
  phone: string | null;
  invited_by_student_id: number | null;
  converted_student_id: number | null;
  notes: string | null;
  created_at: string | null;
};

type VisitorListResponse = {
  data: VisitorResource[];
};

type VisitorResponse = {
  data: VisitorResource;
};

type VisitorConversionResponse = {
  data: {
    visitor: VisitorResource;
  };
};

export interface SaveClassVisitorInput {
  trimesterId: string;
  date: string;
  name: string;
  whatsapp?: string;
  invitedByStudentId?: string | null;
  notes?: string;
}

const toClassVisitor = (classId: string, lessonId: string, visitor: VisitorResource): ClassVisitorRecord => ({
  id: String(visitor.id),
  name: visitor.full_name,
  whatsapp: visitor.phone ?? '',
  classId,
  createdAt: visitor.created_at?.slice(0, 10) ?? '',
  convertedToStudentId: visitor.converted_student_id ? String(visitor.converted_student_id) : null,
  visits: [
    {
      id: String(visitor.class_lesson_id),
      classId,
      lessonId,
      date: visitor.visited_on ?? visitor.created_at?.slice(0, 10) ?? '',
      notes: visitor.notes ?? undefined,
    },
  ],
});

export const classVisitorService = {
  async listByLesson(classId: string, lessonId: string, trimesterId: string): Promise<ClassVisitorRecord[]> {
    const payload = await apiRequest<VisitorListResponse>(
      `/ebd/classes/${classId}/lessons/${lessonId}/visitors?quarter_id=${trimesterId}`
    );

    return payload.data.map((visitor) => toClassVisitor(classId, lessonId, visitor));
  },

  async createForLesson(classId: string, lessonId: string, input: SaveClassVisitorInput): Promise<ClassVisitorRecord> {
    const payload = await apiRequest<VisitorResponse>(`/ebd/classes/${classId}/lessons/${lessonId}/visitors`, {
      method: 'POST',
      body: JSON.stringify({
        quarter_id: Number(input.trimesterId),
        taught_on: input.date,
        full_name: input.name.trim(),
        phone: input.whatsapp?.trim() || null,
        invited_by_student_id: input.invitedByStudentId ? Number(input.invitedByStudentId) : null,
        notes: input.notes?.trim() || null,
      }),
    });

    return toClassVisitor(classId, lessonId, payload.data);
  },

  async removeFromLesson(classId: string, lessonId: string, trimesterId: string, visitorId: string): Promise<void> {
    await apiRequest<void>(
      `/ebd/classes/${classId}/lessons/${lessonId}/visitors/${visitorId}?quarter_id=${trimesterId}`,
      {
        method: 'DELETE',
      }
    );
  },

  async convertToStudent(
    classId: string,
    lessonId: string,
    trimesterId: string,
    visitorId: string,
    enrolledAt: string
  ): Promise<ClassVisitorRecord> {
    const payload = await apiRequest<VisitorConversionResponse>(
      `/ebd/classes/${classId}/lessons/${lessonId}/visitors/${visitorId}/convert-to-student`,
      {
        method: 'POST',
        body: JSON.stringify({
          quarter_id: Number(trimesterId),
          enrolled_at: enrolledAt || null,
        }),
      }
    );

    return toClassVisitor(classId, lessonId, payload.data.visitor);
  },
};
